import { create } from 'zustand';
import { notificationApi } from '../services/notification';
import type { Notification, NotificationListResponse, UnreadCountResponse } from '../types/notification';

interface NotificationState {
  // 状态
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  page: number;
  total: number;

  // Actions
  fetchNotifications: (page?: number, pageSize?: number) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  addNotification: (notification: Notification) => void;
  clearNotifications: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  // 初始状态
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  hasMore: true,
  page: 1,
  total: 0,

  // 获取通知列表
  fetchNotifications: async (page = 1, pageSize = 20) => {
    set({ isLoading: true });
    try {
      const response = await notificationApi.getNotifications({ page, page_size: pageSize });
      // 后端返回的结构：{ code, message, data: { items, total, page, page_size, total_pages } }
      const listData = response.data as NotificationListResponse;
      const items = listData.items || [];
      
      set({
        notifications: page === 1 ? items : [...get().notifications, ...items],
        hasMore: items.length === pageSize && listData.page < listData.total_pages,
        page,
        total: listData.total || items.length,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // 获取未读数
  fetchUnreadCount: async () => {
    try {
      const response = await notificationApi.getUnreadCount();
      // 后端返回的结构：{ code, message, data: { count: number } }
      const data = response.data as UnreadCountResponse;
      set({
        unreadCount: data?.count || 0,
      });
    } catch (error) {
      throw error;
    }
  },

  // 标记已读
  markAsRead: async (notificationId: string) => {
    try {
      await notificationApi.markAsRead(notificationId);
      
      // 更新本地状态
      set({
        notifications: get().notifications.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        ),
      });
      
      // 重新获取未读数
      get().fetchUnreadCount();
    } catch (error) {
      throw error;
    }
  },

  // 全部标记已读
  markAllAsRead: async () => {
    try {
      await notificationApi.markAllAsRead();
      
      // 更新本地状态
      set({
        notifications: get().notifications.map((n) => ({ ...n, is_read: true })),
        unreadCount: 0,
      });
    } catch (error) {
      throw error;
    }
  },

  // 添加新通知（用于WebSocket）
  addNotification: (notification: Notification) => {
    set({
      notifications: [notification, ...get().notifications],
      unreadCount: get().unreadCount + 1,
    });
  },

  // 清除通知
  clearNotifications: () => {
    set({
      notifications: [],
      unreadCount: 0,
      hasMore: true,
      page: 1,
      total: 0,
    });
  },
}));
